import React, { useEffect, useState, useRef } from "react";
import useForm from "../../../../hooks/useForm";
import Button from "../../../form/Button";
import { postAddEdit, postDetails } from "../../../../services/postService";
import { useMutation, useQuery } from "@tanstack/react-query";
import Input from "../../../form/Input";
import { toast } from "react-toastify";
import {
  organizationList,
  taxonomyList,
} from "../../../../services/taxonomyService";
import Editor from "../../../elements/tiptap-editor/Editor";
import Textarea from "../../../form/Textarea";
import Tags from "../../../form/Tags";
import { useNavigate, useParams } from "react-router-dom";
import SelectSingleImage from "../../../form/SelectSingleImage";
import SelectImageGallery from "../../../form/SelectImageGallery";
import FlexibleSelect from "../../../form/select/FlexibleSelect";
import DatePicker from "../../../form/DatePicker";
import { generateSlug, supportedLanguages } from "../../../../helpers";
import LanguageTabs from "../../../elements/LanguageSwitcher";

// Enhanced validation rules
const validation = {
  title: { required: true, message: "Title is required" },
  jobType: { required: true, message: "Job type is required" },
  department: { required: true, message: "Department is required" },
  jobExperienceLevel: {
    required: true,
    message: "Experience level is required",
  },
  status: { required: true, message: "Status is required" },
  locationType: { required: true, message: "Location type is required" },
};

const ManageJob = ({ getResponse = () => {}, close = () => {} }) => {
  const params = useParams();
  const navigate = useNavigate();
  const id = params?.id;
  const userLangStorage = localStorage.getItem("lang") || "en";

  const [tags, setTags] = useState([]);
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [coverMedia, setCoverMedia] = useState(null);
  const [categories, setCategories] = useState([]);
  const [salaryRange, setSalaryRange] = useState("");
  const [organization, setOrganization] = useState("");
  const [skills, setSkills] = useState([]);
  const [locationType, setLocationType] = useState("onsite");
  const [locations, setLocations] = useState([]);
  const [currentLang, setCurrentLang] = useState(userLangStorage);
  const [customErrors, setCustomErrors] = useState({});

  // ✅ Use ref to track if data has been populated
  const isDataPopulated = useRef(false);

  const handleLangChange = (lang) => {
    setCurrentLang(lang);
  };

  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({
    validationRules: validation,
  });

  const { data: details } = useQuery({
    queryKey: ["post-details", id],
    queryFn: async () => {
      const res = await postDetails({ id });
      return res?.data;
    },
    enabled: !!id,
  });

  // ✅ Populate form fields when details are loaded
  useEffect(() => {
    if (details?._id && !isDataPopulated.current) {
      isDataPopulated.current = true;

      // Build content object for all supported languages
      const contentObj = supportedLanguages.reduce((acc, lang) => {
        acc[lang.code] = {
          title: details?.content?.[lang.code]?.title || "",
          content: details?.content?.[lang.code]?.content || "",
          excerpt: details?.content?.[lang.code]?.excerpt || "",
          seo: details?.content?.[lang.code]?.seo || { keywords: [] },
        };
        return acc;
      }, {});

      // ✅ Extract IDs from nested objects
      const departmentId = details?.job?.department?._id || "";
      const experienceId = details?.job?.experience?._id || "";
      const budgetId = details?.job?.budget?._id || "";
      const orgId =
        typeof details?.organization === "string"
          ? details.organization
          : details?.organization?._id || "";

      // ✅ Set all form values
      setFieldsValue({
        ...details,
        content: contentObj,
        title: details?.content?.[currentLang]?.title || "",
        department: departmentId,
        jobType: details?.job?.jobType || "",
        jobExperienceLevel: experienceId,
        jobSkills: details?.job?.skills?.map((skill) => skill._id) || [],
        opening: details?.job?.opening || 1,
        status: details?.status || "draft",
        createdAt: details?.schedule?.startDate || details?.createdAt,
        slug: details?.slug,
      });

      // ✅ Set location data
      if (details?.location && Array.isArray(details.location)) {
        setLocations(
          details.location.map((loc) => ({
            name: loc?.name || "",
            address1: loc?.address1 || "",
            address2: loc?.address2 || "",
            city: loc?.city || "",
            state: loc?.state || "",
            country: loc?.country || "",
            zipcode: loc?.zipcode || "",
            contactPhone: loc?.contactPhone || "",
            landmark: loc?.landmark || "",
            coordinates: loc?.coordinates || [null, null],
            defaultAddress: loc?.defaultAddress || false,
            type: loc?.type || "Point",
          })),
        );
      }

      // ✅ Set location type
      setLocationType(details?.locationType || "onsite");

      // ✅ Set categories (array of IDs)
      if (details?.categories && Array.isArray(details.categories)) {
        const categoryIds = details.categories
          .map((cat) => (typeof cat === "string" ? cat : cat?._id))
          .filter(Boolean);
        setCategories(categoryIds);
      }

      // ✅ Set salary range/budget
      if (budgetId) {
        setSalaryRange(budgetId);
      }

      // ✅ Set organization
      if (orgId) {
        setOrganization(orgId);
      }

      // ✅ Set skills (array of IDs)
      if (details?.job?.skills && Array.isArray(details.job.skills)) {
        const skillIds = details.job.skills
          .map((skill) => (typeof skill === "string" ? skill : skill?._id))
          .filter(Boolean);
        setSkills(skillIds);
      }

      // ✅ Set cover media
      if (details?.coverMedia) {
        setCoverMedia(details.coverMedia);
      }

      // ✅ Set media gallery
      if (details?.mediaGallery && Array.isArray(details.mediaGallery)) {
        setSelectedMedia(details.mediaGallery);
      }

      // ✅ Set tags
      if (details?.tagDetails && Array.isArray(details.tagDetails)) {
        setTags(
          details.tagDetails.map((item) => ({
            label:
              item?.content?.[userLangStorage]?.title ||
              item?.content?.en?.title ||
              "",
            _id: item._id,
          })),
        );
      }
    }
  }, [details, currentLang, userLangStorage]);

  // ✅ Reset ref when ID changes
  useEffect(() => {
    isDataPopulated.current = false;
  }, [id]);

  // Custom validation function
  const validateForm = () => {
    const newErrors = {};

    if (!values?.content?.[currentLang]?.title) {
      newErrors.title = "Title is required";
    }

    if (!values?.jobType) {
      newErrors.jobType = "Job type is required";
    }

    if (!values?.department) {
      newErrors.department = "Department is required";
    }

    if (!values?.jobExperienceLevel) {
      newErrors.jobExperienceLevel = "Experience level is required";
    }

    if (!values?.status) {
      newErrors.status = "Status is required";
    }

    if (!locationType) {
      newErrors.locationType = "Location type is required";
    }

    if (locationType !== "remote" && (!locations || locations.length === 0)) {
      newErrors.locations =
        "At least one location is required for non-remote jobs";
    }

    if (locationType !== "remote" && locations && locations.length > 0) {
      locations.forEach((loc, index) => {
        if (!loc.name || loc.name.trim() === "") {
          newErrors[`location_${index}_name`] =
            `Location name is required for Address ${index + 1}`;
          newErrors.locations = "Location name is required for all addresses";
        }
      });
    }

    if (!skills || skills.length === 0) {
      newErrors.skills = "At least one skill is required";
    }

    setCustomErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      const formData = new FormData();

      formData.append("type", "standard-job");
      formData.append("title", "job-post");

      // Add multi-language content
      supportedLanguages.forEach((lang) => {
        if (val?.content?.[lang.code]?.title) {
          formData.append(
            `content[${lang.code}][title]`,
            val.content[lang.code].title,
          );
        }
        if (val?.content?.[lang.code]?.content) {
          formData.append(
            `content[${lang.code}][content]`,
            val.content[lang.code].content,
          );
        }
        if (val?.content?.[lang.code]?.excerpt) {
          formData.append(
            `content[${lang.code}][excerpt]`,
            val.content[lang.code].excerpt,
          );
        }
      });

      // Add job fields
      formData.append("job[jobType]", val?.jobType);
      formData.append("job[jobMode]", "standard");
      formData.append("job[department]", val?.department);
      formData.append("job[experience]", val?.jobExperienceLevel);
      formData.append("job[opening]", val?.opening || 1);

      // Add skills array
      if (skills && Array.isArray(skills) && skills.length > 0) {
        skills.forEach((skill, index) => {
          const skillId =
            typeof skill === "object"
              ? skill?.value || skill?._id || skill
              : skill;
          if (skillId && skillId !== "undefined") {
            formData.append(`job[skills][${index}]`, skillId);
          }
        });
      }

      // Add budget/salary range
      if (salaryRange) {
        formData.append("job[budget]", salaryRange);
      }

      // Add organization
      if (organization) {
        formData.append("organization", organization);
      }

      // Add status
      formData.append("status", val?.status);

      // Add cover media
      if (coverMedia?._id) {
        formData.append("coverMedia", coverMedia._id);
      }

      // Add location type
      formData.append("locationType", locationType);

      // Add locations
      if (locationType !== "remote" && locations && locations.length > 0) {
        locations.forEach((loc, index) => {
          if (loc.name && loc.name.trim() !== "") {
            formData.append(`location[${index}][name]`, loc.name.trim());
          }
          if (loc.address1 && loc.address1.trim() !== "") {
            formData.append(
              `location[${index}][address1]`,
              loc.address1.trim(),
            );
          }
          if (loc.address2 && loc.address2.trim() !== "") {
            formData.append(
              `location[${index}][address2]`,
              loc.address2.trim(),
            );
          }
          if (loc.city && loc.city.trim() !== "") {
            formData.append(`location[${index}][city]`, loc.city.trim());
          }
          if (loc.state && loc.state.trim() !== "") {
            formData.append(`location[${index}][state]`, loc.state.trim());
          }
          if (loc.country && loc.country.trim() !== "") {
            formData.append(`location[${index}][country]`, loc.country.trim());
          }
          if (loc.zipcode && loc.zipcode.trim() !== "") {
            formData.append(`location[${index}][zipcode]`, loc.zipcode.trim());
          }
          if (loc.contactPhone && loc.contactPhone.trim() !== "") {
            formData.append(
              `location[${index}][contactPhone]`,
              loc.contactPhone.trim(),
            );
          }
          if (loc.landmark && loc.landmark.trim() !== "") {
            formData.append(
              `location[${index}][landmark]`,
              loc.landmark.trim(),
            );
          }
          if (
            loc.coordinates &&
            loc.coordinates[0] !== null &&
            loc.coordinates[0] !== ""
          ) {
            formData.append(
              `location[${index}][coordinates][0]`,
              loc.coordinates[0],
            );
          }
          if (
            loc.coordinates &&
            loc.coordinates[1] !== null &&
            loc.coordinates[1] !== ""
          ) {
            formData.append(
              `location[${index}][coordinates][1]`,
              loc.coordinates[1],
            );
          }
        });
      }

      // Add media gallery
      if (
        selectedMedia &&
        Array.isArray(selectedMedia) &&
        selectedMedia.length > 0
      ) {
        selectedMedia.forEach((media, index) => {
          const mediaId = typeof media === "object" ? media?._id : media;
          if (mediaId && mediaId !== "undefined") {
            formData.append(`mediaGallery[${index}]`, mediaId);
          }
        });
      }

      // Add schedule start date
      if (val?.createdAt) {
        formData.append("scheduleStartDate", val.createdAt);
      }

      // Add ID and slug if updating
      if (details?._id) {
        formData.append("id", details._id);
        if (val?.slug) {
          formData.append("slug", val.slug);
        }
      }

      return await postAddEdit(formData);
    },

    onSuccess: (res) => {
      getResponse(res);
      if (res?.status === 200 || res?.status === 201) {
        toast.success(
          details?._id
            ? "Job updated successfully"
            : "Job created successfully",
        );
        navigate(`/admin/jobs`);
      } else if (res?.status === 500) {
        toast.error("Server Error");
      } else {
        toast.error(res?.message || res?.error);
      }
      return res;
    },
    onError: (error) => {
      toast.error("An error occurred while saving the job");
    },
  });

  const { data: jobDepartmentData } = useQuery({
    queryKey: ["department"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "department",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: jobExperienceData } = useQuery({
    queryKey: ["experience"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "experience",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: jobSkillData } = useQuery({
    queryKey: ["skill"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "skill",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: salaryData } = useQuery({
    queryKey: ["salary"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "salary-range",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: organizationData } = useQuery({
    queryKey: ["organization"],
    queryFn: async () => {
      const res = await organizationList({
        limit: 10000,
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: categoryData } = useQuery({
    queryKey: ["category"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "standard-job-category",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const { data: tagsData } = useQuery({
    queryKey: ["tags"],
    queryFn: async () => {
      const res = await taxonomyList({
        limit: 10000,
        type: "standard-job-tag",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const handleLocationChange = (e, index) => {
    const { name, value } = e.target;
    const field = name.split("-")[0];
    setLocations((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    if (field === "name" && customErrors[`location_${index}_name`]) {
      const newErrors = { ...customErrors };
      delete newErrors[`location_${index}_name`];
      if (value.trim() !== "") {
        delete newErrors.locations;
      }
      setCustomErrors(newErrors);
    }
  };

  const handleLatLngChange = (e, index) => {
    const { name, value } = e.target;
    setLocations((prev) => {
      const updated = [...prev];
      const coords = updated[index].coordinates || [null, null];
      if (name === "latitude") coords[0] = value ? parseFloat(value) : null;
      if (name === "longitude") coords[1] = value ? parseFloat(value) : null;
      updated[index] = { ...updated[index], coordinates: coords };
      return updated;
    });
  };

  const handleSubmitWrapper = (event) => {
    event.preventDefault();

    const completeValues = {
      ...values,
      skills: skills,
      locationType: locationType,
      locations: locations,
      coverMedia: coverMedia,
      selectedMedia: selectedMedia,
      salaryRange: salaryRange,
      organization: organization,
    };

    setFieldsValue(completeValues);

    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    mutate(completeValues);
  };

  return (
    <>
      <form onSubmit={handleSubmitWrapper}>
        <div className="flex justify-between items-center w-full px-6">
          <div className="font-normal text-lg xl:text-3xl text-primary">
            {details?._id ? "Update Job" : "Create Job"}
          </div>
          <div className="flex gap-4">
            <Button
              buttonType="submit"
              buttonLabel={details?._id ? "Update Job" : "Save Job"}
              buttonClasses="min-w-28"
              buttonLabelClasses="mx-auto"
              {...(isLoading && {
                loading: true,
                buttonIconPosition: "left",
                isDisable: true,
              })}
            />
            <Button
              buttonLabel="Cancel"
              buttonClasses="min-w-28 !bg-white !text-primary border !border-primary"
              buttonLabelClasses="mx-auto"
              buttonFunction={() => navigate("/admin/jobs")}
            />
          </div>
        </div>

        <div className="grid grid-cols-4 gap-6 p-6">
          <div className="col-span-3 space-y-4">
            <div>
              <Input
                label="Title"
                inputPlaceholder="Enter title"
                onChange={(e) => {
                  const newValue = e.target.value;
                  setFieldsValue({
                    ...values,
                    title: newValue,
                    content: {
                      ...values.content,
                      [currentLang]: {
                        ...values.content?.[currentLang],
                        title: newValue,
                      },
                    },
                  });
                  if (customErrors.title) {
                    setCustomErrors({ ...customErrors, title: "" });
                  }
                }}
                inputName={"title"}
                required={true}
                value={values?.content?.[currentLang]?.title || ""}
                {...formValidate(errors, "title")}
              />
              {customErrors.title && (
                <p className="text-red-500 text-sm mt-1">
                  {customErrors.title}
                </p>
              )}
            </div>

            <Editor
              label={"Content"}
              height={200}
              value={values?.content?.[currentLang]?.content || ""}
              placeholder={"Write Something"}
              onChange={(val) => {
                setFieldsValue({
                  ...values,
                  content: {
                    ...values.content,
                    [currentLang]: {
                      ...values.content?.[currentLang],
                      content: val,
                    },
                  },
                });
              }}
            />

            <Textarea
              label="Excerpt"
              inputPlaceholder="Write excerpt"
              inputClasses="!h-28"
              inputName="excerpt"
              inputValue={values?.content?.[currentLang]?.excerpt || ""}
              onChange={(e) => {
                const newValue = e.target.value;
                setFieldsValue({
                  ...values,
                  content: {
                    ...values.content,
                    [currentLang]: {
                      ...values.content?.[currentLang],
                      excerpt: newValue,
                    },
                  },
                });
              }}
            />

            {/* <div className="bg-white p-4 rounded-md border border-text-secondary/50">
              <SelectImageGallery
                label="Gallery"
                values={selectedMedia}
                onChange={(newMedia) => setSelectedMedia(newMedia)}
                isSingle={false}
              />
            </div> */}

            <div>
              <FlexibleSelect
                label="Location Type"
                placeholder="Choose Location Type"
                onChange={(e) => {
                  setLocationType(e);
                  if (e === "remote") setLocations([]);
                  if (customErrors.locationType) {
                    setCustomErrors({ ...customErrors, locationType: "" });
                  }
                }}
                selectedValue={locationType}
                dropdownData={[
                  { name: "Onsite", value: "onsite" },
                  { name: "Remote", value: "remote" },
                  { name: "Hybrid", value: "hybrid" },
                ]}
              />
              {customErrors.locationType && (
                <p className="text-red-500 text-sm mt-1">
                  {customErrors.locationType}
                </p>
              )}
            </div>

            {locationType !== "remote" && (
              <div className="space-y-4 mt-4">
                {locations.map((loc, index) => (
                  <div
                    key={index}
                    className="p-4 border border-gray-300 rounded-md space-y-2 bg-gray-50"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <h4 className="text-sm font-semibold">
                        Address {index + 1}
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          setLocations((prev) =>
                            prev.filter((_, i) => i !== index),
                          );
                          if (locations.length > 1 && customErrors.locations) {
                            setCustomErrors({ ...customErrors, locations: "" });
                          }
                        }}
                        className="text-red-500 text-xs"
                      >
                        <i className="fas fa-trash"></i> Remove
                      </button>
                    </div>

                    <div>
                      <Input
                        label="Name"
                        inputName="name"
                        value={loc.name || ""}
                        onChange={(e) => handleLocationChange(e, index)}
                        required={true}
                      />
                      {customErrors[`location_${index}_name`] && (
                        <p className="text-red-500 text-sm mt-1">
                          {customErrors[`location_${index}_name`]}
                        </p>
                      )}
                    </div>
                    <Input
                      label="Address 1"
                      inputName="address1"
                      value={loc.address1 || ""}
                      onChange={(e) => handleLocationChange(e, index)}
                    />
                    <Input
                      label="Address 2"
                      inputName="address2"
                      value={loc.address2 || ""}
                      onChange={(e) => handleLocationChange(e, index)}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="City"
                        inputName="city"
                        value={loc.city || ""}
                        onChange={(e) => handleLocationChange(e, index)}
                      />
                      <Input
                        label="State"
                        inputName="state"
                        value={loc.state || ""}
                        onChange={(e) => handleLocationChange(e, index)}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Country"
                        inputName="country"
                        value={loc.country || ""}
                        onChange={(e) => handleLocationChange(e, index)}
                      />
                      <Input
                        label="Zipcode"
                        inputName="zipcode"
                        value={loc.zipcode || ""}
                        onChange={(e) => handleLocationChange(e, index)}
                      />
                    </div>

                    <Input
                      label="Phone"
                      inputName="contactPhone"
                      value={loc.contactPhone || ""}
                      onChange={(e) => handleLocationChange(e, index)}
                    />
                    <Input
                      label="Landmark"
                      inputName="landmark"
                      value={loc.landmark || ""}
                      onChange={(e) => handleLocationChange(e, index)}
                    />

                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        label="Coordinates (Latitude)"
                        inputName="latitude"
                        inputType="number"
                        value={loc.coordinates?.[0] || ""}
                        onChange={(e) => handleLatLngChange(e, index)}
                      />
                      <Input
                        label="Coordinates (Longitude)"
                        inputName="longitude"
                        inputType="number"
                        value={loc.coordinates?.[1] || ""}
                        onChange={(e) => handleLatLngChange(e, index)}
                      />
                    </div>

                    {/* <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`defaultAddress-${index}`}
                        checked={loc.defaultAddress || false}
                        onChange={() =>
                          setLocations((prev) => {
                            const updated = [...prev];
                            updated[index].defaultAddress =
                              !updated[index].defaultAddress;
                            return updated;
                          })
                        }
                      />
                      <label
                        htmlFor={`defaultAddress-${index}`}
                        className="text-sm"
                      >
                        {loc.defaultAddress
                          ? "Default Address"
                          : "Set as Default Address"}
                      </label>
                    </div> */}
                  </div>
                ))}

                <Button
                  buttonLabel="+ Add Location"
                  buttonFunction={() => {
                    setLocations([
                      ...locations,
                      {
                        name: "",
                        address1: "",
                        address2: "",
                        city: "",
                        state: "",
                        country: "",
                        zipcode: "",
                        contactPhone: "",
                        landmark: "",
                        coordinates: [null, null],
                        defaultAddress: false,
                        type: "Point",
                      },
                    ]);
                    if (customErrors.locations) {
                      setCustomErrors({ ...customErrors, locations: "" });
                    }
                  }}
                  buttonClasses="!bg-secondary !text-white"
                />
                {customErrors.locations && (
                  <p className="text-red-500 text-sm mt-1">
                    {customErrors.locations}
                  </p>
                )}
              </div>
            )}

            <div className="relative col-span-4 mt-5">
              <div className="flex gap-4 mt-10">
                <Button
                  buttonType="submit"
                  buttonLabel={details?._id ? "Update Job" : "Save Job"}
                  buttonClasses={"min-w-28"}
                  buttonLabelClasses={"mx-auto"}
                  {...(isLoading && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
                <Button
                  buttonLabel={"Cancel"}
                  buttonClasses={
                    "min-w-28 !bg-white !text-primary border !border-primary"
                  }
                  buttonLabelClasses={"mx-auto"}
                  buttonFunction={() => navigate("/admin/jobs")}
                />
              </div>
            </div>
          </div>

          <div className="col-span-1 space-y-6">
            <div className="border border-gray-300 rounded-lg p-4 space-y-2">
              <SelectSingleImage
                label="Upload Featured Image"
                size="lg"
                value={coverMedia}
                onChange={(img) => setCoverMedia(img)}
                onRemove={() => setCoverMedia(null)}
              />
            </div>

            <div className="border border-gray-300 rounded-lg p-4 space-y-2">
              <DatePicker
                label={"Publish Date"}
                placeholder={"Choose publish date"}
                position="fixed"
                onChange={(e) =>
                  handleChange({
                    target: {
                      name: "createdAt",
                      value: e?.startDate || e?.endDate,
                    },
                  })
                }
                value={{
                  startDate: values?.createdAt,
                  endDate: values?.createdAt,
                }}
                {...formValidate(errors, "createdAt")}
              />

              <div>
                <FlexibleSelect
                  label={"Status"}
                  placeholder={"Choose status"}
                  onChange={(e) => {
                    handleChange({ target: { name: "status", value: e } });
                    if (customErrors.status) {
                      setCustomErrors({ ...customErrors, status: "" });
                    }
                  }}
                  selectedValue={values?.status}
                  dropdownData={[
                    { name: "Active", value: "active" },
                    { name: "Inactive", value: "inactive" },
                  ]}
                  {...formValidate(errors, "status")}
                />
                {customErrors.status && (
                  <p className="text-red-500 text-sm mt-1">
                    {customErrors.status}
                  </p>
                )}
              </div>

              <FlexibleSelect
                label={"Company"}
                dropdownData={organizationData?.data?.map((item) => {
                  return {
                    name: item?.content?.en?.title || item?.name,
                    value: item?._id,
                  };
                })}
                selectedValue={organization}
                onChange={setOrganization}
                placeholder="Choose Company"
              />

              {/* <FlexibleSelect
                label={"Job Categories"}
                isSearch={true}
                dropdownData={categoryData?.data?.map((item) => {
                  return {
                    name: item?.content?.[userLangStorage]?.title || item?.content?.en?.title,
                    value: item?._id,
                  };
                })}
                selectedValue={categories}
                onChange={(val) => {
                  console.log("Categories selected:", val);
                  setCategories(val);
                }}
                type="multiple"
                placeholder="Choose category"
              /> */}

              <div>
                <FlexibleSelect
                  label={"Job Type"}
                  placeholder={"Choose job type"}
                  onChange={(e) => {
                    handleChange({ target: { name: "jobType", value: e } });
                    if (customErrors.jobType) {
                      setCustomErrors({ ...customErrors, jobType: "" });
                    }
                  }}
                  selectedValue={values?.jobType}
                  dropdownData={[
                    { name: "Full Time", value: "full-time" },
                    { name: "Part Time", value: "part-time" },
                    { name: "Contract", value: "contract" },
                    { name: "Internship", value: "internship" },
                    { name: "Remote", value: "remote" },
                  ]}
                  {...formValidate(errors, "jobType")}
                />
                {customErrors.jobType && (
                  <p className="text-red-500 text-sm mt-1">
                    {customErrors.jobType}
                  </p>
                )}
              </div>

              <div>
                <FlexibleSelect
                  label={"Job Departments"}
                  placeholder={"Choose departments"}
                  onChange={(e) => {
                    handleChange({ target: { name: "department", value: e } });
                    if (customErrors.department) {
                      setCustomErrors({ ...customErrors, department: "" });
                    }
                  }}
                  selectedValue={values?.department}
                  dropdownData={jobDepartmentData?.data?.map((item) => {
                    return {
                      name:
                        item?.content?.[userLangStorage]?.title ||
                        item?.content?.en?.title,
                      value: item?._id,
                    };
                  })}
                  {...formValidate(errors, "department")}
                />
                {customErrors.department && (
                  <p className="text-red-500 text-sm mt-1">
                    {customErrors.department}
                  </p>
                )}
              </div>

              <FlexibleSelect
                label={"Salary Range"}
                dropdownData={salaryData?.data?.map((item) => {
                  return {
                    name: `${item?.range?.unit === "usd" ? "$" : "₹"}${
                      item?.range?.min >= 1000
                        ? item?.range?.min / 1000 + "k"
                        : item?.range?.min
                    } - ${item?.range?.unit === "usd" ? "$" : "₹"}${
                      item?.range?.max >= 1000
                        ? item?.range?.max / 1000 + "k"
                        : item?.range?.max
                    }`,
                    value: item?._id,
                  };
                })}
                selectedValue={salaryRange}
                onChange={setSalaryRange}
                placeholder="Choose Salary Range"
              />

              <div>
                <FlexibleSelect
                  label={"Experience Level"}
                  placeholder={"Choose experience level"}
                  onChange={(e) => {
                    handleChange({
                      target: { name: "jobExperienceLevel", value: e },
                    });
                    if (customErrors.jobExperienceLevel) {
                      setCustomErrors({
                        ...customErrors,
                        jobExperienceLevel: "",
                      });
                    }
                  }}
                  selectedValue={values?.jobExperienceLevel}
                  dropdownData={jobExperienceData?.data?.map((item) => {
                    return {
                      name:
                        item?.content?.[userLangStorage]?.title ||
                        item?.content?.en?.title,
                      value: item?._id,
                    };
                  })}
                  {...formValidate(errors, "jobExperienceLevel")}
                />
                {customErrors.jobExperienceLevel && (
                  <p className="text-red-500 text-sm mt-1">
                    {customErrors.jobExperienceLevel}
                  </p>
                )}
              </div>

              <div>
                <FlexibleSelect
                  label={"Skills"}
                  isSearch={true}
                  dropdownData={jobSkillData?.data?.map((item) => {
                    return {
                      name:
                        item?.content?.[userLangStorage]?.title ||
                        item?.content?.en?.title,
                      value: item?._id,
                    };
                  })}
                  selectedValue={skills}
                  onChange={(val) => {
                    const skillIds = Array.isArray(val)
                      ? val.map((skill) =>
                          typeof skill === "object"
                            ? skill?.value || skill?._id
                            : skill,
                        )
                      : [];
                    setSkills(skillIds);
                    if (customErrors.skills) {
                      setCustomErrors({ ...customErrors, skills: "" });
                    }
                  }}
                  type="multiple"
                  placeholder="Choose skills"
                />
                {customErrors.skills && (
                  <p className="text-red-500 text-sm mt-1">
                    {customErrors.skills}
                  </p>
                )}
              </div>

              <Input
                label="Number of Openings"
                inputPlaceholder="Enter position opened"
                inputType="number"
                onChange={handleChange}
                inputName={"opening"}
                required={false}
                value={values?.opening || 1}
              />

              {/* <Tags
                label="Tags"
                placeholder="Enter tags"
                options={tagsData?.data?.map((item) => ({
                  label:
                    item?.content?.[userLangStorage]?.title ||
                    item?.content?.en?.title,
                  _id: item._id,
                }))}
                selectedItems={tags}
                setSelectedItems={setTags}
                tagsType="input"
              /> */}
            </div>
          </div>
        </div>
      </form>
    </>
  );
};

export default ManageJob;
